<template>
  <v-card>
    <v-container class="py-0" fluid>
      <v-row align-content="space-around">
        <v-col cols="12" sm="1">
          <v-card-title class="pa-0">
            <div>
              <div class="headline">All Cases</div>
              <div class="text-subtitle-1 font-weight-thin">
                {{ filteredCases.length }} Cases
              </div>
            </div>
          </v-card-title>
        </v-col>
        <v-col cols="12" sm="3">
          <div class="total-settlement-value font-weight-thin pt-5">
            {{ inventoryValue.totalLowValue }} -
            {{ inventoryValue.totalHighValue }}
          </div>
          <div class="total-fee-value">
            {{ inventoryValue.totalLowFees }} -
            {{ inventoryValue.totalHighFees }} (33%)
          </div>
        </v-col>
        <v-col cols="12" sm="4">
          <div>
            <highcharts ref="chart" :options="rankChartOptions" />
          </div>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="1">
          <v-switch
            class=""
            label="Exclude Ref"
            v-model="filterRefAndInactive"
          ></v-switch>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            hide-details
            single-line
          ></v-text-field>
        </v-col>
      </v-row>
    </v-container>
    <v-data-table
      :footer-props="{
        'items-per-page-options': [50, 100, -1]
      }"
      :headers="headers"
      :items="filteredCases"
      :item-key="Math.random(10).toString()"
      :loading="$apollo.loading"
      :search="search"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      ref="inventory-table"
    >
      <template v-slot:item.caseId="props">
        <a
          class="go-to-case-link mr-2"
          target="_blank"
          :href="
            `https://cs-web.airdesksolutions.com/SA/CaseSummary.aspx?CaseID=${props.item.caseId}`
          "
        >
          <v-icon size="15">mdi-open-in-new</v-icon>
        </a>
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon x-small v-bind="attrs" v-on="on">
              <v-icon size="15">mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item v-if="isAdmin">
              <v-list-item-title>
                <a
                  v-if="isWatching(currentUser, props.item.caseId)"
                  @click="stopWatching($apollo, props.item.caseId)"
                >
                  <v-icon size="15" class="mr-2">mdi-star-off</v-icon>
                  Stop Watching
                </a>
                <a v-else @click="startWatching($apollo, props.item.caseId)">
                  <v-icon size="15" class="mr-2">mdi-star</v-icon>
                  Start Watching
                </a>
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>
                <a
                  title="Open Investigation"
                  @click="
                    investigationDialog = true;
                    selectedCaseId = props.item.caseId;
                  "
                >
                  <v-icon size="15" color="primary" class="mr-2"
                    >mdi-folder-account</v-icon
                  >
                  Investigation Report
                </a>
              </v-list-item-title>
            </v-list-item>
            <v-list-item>
              <v-list-item-title>
                <a
                  @click="
                    selectedCaseId = props.item.caseId;
                    mileageReimbursementDialog = true;
                  "
                >
                  <v-icon size="15" class="mr-2"
                    >mdi-map-marker-distance</v-icon
                  >
                  Mileage Reimbursement
                </a>
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.status="props">
        <v-edit-dialog
          @open="openEditDialog(props.item.caseId)"
          @close="closeEditDialog"
        >
          {{ props.item.status && props.item.status.substr(0, 20) }}
          <template v-slot:input>
            <v-select
              :items="statusOptions"
              :value="getValue(props.item.status, statusOptions)"
              @change="saveStatus($event, 'status', statusOptions, 1)"
              dense
            ></v-select>
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:item.lastAccessedDateTime="props">
        {{ formatLastAccessDate(props.item.lastAccessedDateTime) }}
      </template>
      <template v-slot:item.dateOfSolConf="props">
        <v-icon v-if="props.item.dateOfSolConf" size="15"
          >mdi-check-circle</v-icon
        >
      </template>
      <template v-slot:item.valueFrom="props">
        {{ formatValue(props.item) }}
      </template>
      <template v-slot:item.rank="props">
        <v-edit-dialog @open="openRank(props.item.caseId)" @close="closeRank">
          {{ props.item.rank }}
          <template v-slot:input>
            <v-select
              :items="rankOptions"
              :value="rankValue(props.item.rank)"
              @change="saveRank"
              dense
            ></v-select>
          </template>
        </v-edit-dialog>
      </template>
      <template v-slot:header.daysInStatus="{ header }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">{{ header.text }}</span>
          </template>
          <span>Days in Status</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog
      v-model="investigationDialog"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <investigation
        :closeInvestigationDialog="closeInvestigationDialog"
        :caseId="selectedCaseId"
      />
    </v-dialog>

    <v-dialog v-model="mileageReimbursementDialog" max-width="800px">
      <v-card>
        <v-card-title class="headline">
          Mileage Reimbursement
          <v-spacer></v-spacer>
          <v-btn icon @click="mileageReimbursementDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="clientAddressesLoading || medicalProvidersLoading"
            type="info"
            text
          >
            Loading data...
          </v-alert>
          <v-alert v-else-if="clientAddressesError" type="error" text>
            Error loading client addresses: {{ clientAddressesError.message }}
          </v-alert>
          <v-alert v-else-if="medicalProvidersError" type="error" text>
            Error loading medical providers: {{ medicalProvidersError.message }}
          </v-alert>

          <!-- Client Address Selection -->
          <div
            v-if="
              !clientAddressesLoading &&
                !clientAddressesError &&
                clientAddresses.length > 0
            "
          >
            <h3 class="subtitle-1 font-weight-bold mb-2">Client Address</h3>
            <v-card outlined class="mb-6">
              <v-card-text>
                <div
                  v-if="selectedClientAddress"
                  class="d-flex align-center mb-2"
                >
                  <div>
                    <div class="font-weight-medium">
                      {{ selectedClientAddress.client_name }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        formatAddress(
                          selectedClientAddress.address,
                          selectedClientAddress.city,
                          selectedClientAddress.state,
                          selectedClientAddress.zip
                        )
                      }}
                    </div>
                  </div>
                  <v-spacer></v-spacer>
                  <v-btn
                    small
                    text
                    color="primary"
                    @click="
                      showClientAddressSelector = !showClientAddressSelector
                    "
                  >
                    Change
                  </v-btn>
                </div>

                <v-expand-transition>
                  <div v-if="showClientAddressSelector">
                    <v-radio-group
                      v-model="selectedClientAddress"
                      :mandatory="true"
                    >
                      <v-radio
                        v-if="clientPrimaryAddress"
                        :label="
                          formatAddress(
                            clientPrimaryAddress.p_address,
                            clientPrimaryAddress.p_city,
                            clientPrimaryAddress.p_state,
                            clientPrimaryAddress.p_zip
                          ) + ' (Primary)'
                        "
                        :value="{
                          client_name: clientPrimaryAddress.client_name,
                          address: clientPrimaryAddress.p_address,
                          city: clientPrimaryAddress.p_city,
                          state: clientPrimaryAddress.p_state,
                          zip: clientPrimaryAddress.p_zip,
                          is_primary: true
                        }"
                      ></v-radio>
                      <v-radio
                        v-for="(address, index) in clientAltAddresses"
                        :key="index"
                        :label="
                          formatAddress(
                            address.alt_address,
                            address.alt_city,
                            address.alt_state,
                            address.alt_zip
                          )
                        "
                        :value="{
                          client_name: address.client_name,
                          address: address.alt_address,
                          city: address.alt_city,
                          state: address.alt_state,
                          zip: address.alt_zip,
                          is_primary: false
                        }"
                      ></v-radio>
                    </v-radio-group>
                  </div>
                </v-expand-transition>
              </v-card-text>
            </v-card>

            <v-divider class="mb-4"></v-divider>
          </div>

          <!-- Medical Providers -->
          <div v-if="!medicalProvidersLoading && !medicalProvidersError">
            <h3 class="subtitle-1 font-weight-bold mb-2">Medical Providers</h3>
            <div v-if="medicalProviders.length === 0">
              No medical providers found for this case.
            </div>
            <v-expansion-panels v-else>
              <v-expansion-panel
                v-for="(provider, index) in groupedProviders"
                :key="index"
              >
                <v-expansion-panel-header>
                  <div>
                    <strong>{{ provider.provider_name }}</strong>
                    <span class="ml-2 grey--text text-caption"
                      >({{ provider.visits }} visits)</span
                    >
                    <v-chip
                      v-if="!hasValidAddress(provider)"
                      x-small
                      color="orange"
                      text-color="white"
                      class="ml-2"
                    >
                      No Address
                    </v-chip>
                    <div class="text-caption grey--text">
                      Selected: {{ getSelectedAddressDisplay(provider) }}
                    </div>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-radio-group
                    v-model="selectedAddresses[provider.provider_name]"
                    :mandatory="false"
                  >
                    <v-radio
                      :label="
                        formatAddress(
                          provider.provider_address,
                          provider.provider_city,
                          provider.provider_state,
                          provider.provider_zip
                        ) + ' (Primary)'
                      "
                      :value="{
                        provider_name: provider.provider_name,
                        address: provider.provider_address,
                        city: provider.provider_city,
                        state: provider.provider_state,
                        zip: provider.provider_zip,
                        visits: provider.visits,
                        is_primary: true
                      }"
                    ></v-radio>
                    <v-radio
                      v-for="(address, addressIndex) in provider.addresses"
                      :key="addressIndex"
                      :label="
                        formatAddress(
                          address.alt_address,
                          address.alt_city,
                          address.alt_state,
                          address.alt_zip
                        )
                      "
                      :value="{
                        provider_name: provider.provider_name,
                        address: address.alt_address,
                        city: address.alt_city,
                        state: address.alt_state,
                        zip: address.alt_zip,
                        visits: address.visits,
                        is_primary: false
                      }"
                    ></v-radio>
                  </v-radio-group>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="calculateMileageReimbursement"
            :disabled="Object.keys(selectedAddresses).length === 0"
          >
            Calculate Reimbursement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showReimbursementResults" max-width="800px">
      <v-card>
        <v-card-title class="headline">
          Mileage Reimbursement Results
          <v-spacer></v-spacer>
          <v-btn icon @click="showReimbursementResults = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="copySuccess"
            type="success"
            text
            dismissible
            @input="copySuccess = false"
          >
            Results copied to clipboard!
          </v-alert>

          <v-row align="center" class="mb-4">
            <v-col cols="12" sm="6" md="4">
              <v-text-field
                v-model="mileageRate"
                label="Mileage Rate (¢)"
                type="number"
                min="1"
                max="200"
                suffix="¢ per mile"
                outlined
                dense
                @input="recalculateReimbursement"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" md="8">
              <div class="text-caption grey--text">
                Adjust the mileage rate to recalculate reimbursement
              </div>
            </v-col>
          </v-row>

          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Provider</th>
                  <th class="text-right">Visits</th>
                  <th class="text-right">Round Trip Miles</th>
                  <th class="text-right">Reimbursement</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(result, index) in reimbursementResults"
                  :key="index"
                  :class="{ 'grey lighten-5': result.has_blank_address }"
                >
                  <td>
                    {{ result.provider_name }}
                    <v-chip
                      v-if="result.has_blank_address"
                      x-small
                      color="orange"
                      text-color="white"
                      class="ml-2"
                    >
                      No Address
                    </v-chip>
                  </td>
                  <td class="text-right">{{ result.visits }}</td>
                  <td class="text-right">{{ result.round_trip_miles }}</td>
                  <td class="text-right">
                    {{ formatCurrency(result.reimbursement) }}
                  </td>
                </tr>
                <tr class="grey lighten-4 font-weight-bold">
                  <td colspan="3" class="text-right">Total:</td>
                  <td class="text-right">
                    {{ formatCurrency(totalReimbursement) }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div class="mt-4 text-caption">
            Patient Address: {{ patientAddressFromResults }}
          </div>
          <div class="mt-1 text-caption">Rate: {{ mileageRate }}¢ per mile</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            text
            @click="copyReimbursementResultsToClipboard"
          >
            <v-icon left>mdi-content-copy</v-icon>
            Copy to Clipboard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import {
  formatValue,
  startWatching,
  stopWatching,
  isWatching,
  hasRole,
  getDaysSinceToday
} from "../util";
import groupBy from "lodash/groupBy";
import findIndex from "lodash/findIndex";
import filter from "lodash/filter";
import { DASH_INVENTORY_LIST } from "../graphql/user.gql";
import accounting from "accounting";
import Investigation from "./Investigation.vue";

export default {
  data() {
    return {
      search: "",
      filterRefAndInactive: true,
      headers: [
        { text: "Action", value: "caseId", width: "100px" },
        { text: "Type", value: "type" },
        { text: "Stage", value: "stage" },
        { text: "Status", value: "status" },
        { text: "DIS", value: "daysInStatus" },
        { text: "Client", value: "clientName" },
        { text: "Last Access", value: "lastAccessedDateTime" },
        { text: "SOL Conf", value: "dateOfSolConf" },
        // { text: "Value", value: "valueFrom" },
        { text: "Rank", value: "rank" },
        { text: "Paralegal", value: "paralegal" },
        { text: "Opened", value: "dateOpened" },
        { text: "DOI", value: "dateOfIncident" },
        { text: "State", value: "state" }
      ],
      user: { cases: [] },
      rankOptions: [
        { text: "A++ (> $1M)", value: 805 },
        { text: "A+ ($251k - $1M)", value: 806 },
        { text: "A ($101k - $250k)", value: 807 },
        { text: "B ($51k - $100k)", value: 1161 },
        { text: "C ($15k - $50k)", value: 1089 },
        { text: "D (<$15k)", value: 1090 },
        { text: "Unrated - Default", value: 1088 },
        { text: "", value: "" }
      ],
      statusOptions: [
        { stage: "Treating", text: "PL 00 - File Setup", value: 1120 },
        { stage: "Treating", text: "PL 01 - Medical Management", value: 1121 },
        {
          stage: "Demand Prep",
          text: "PL 02 - Evidence Collection",
          value: 1122
        },
        {
          stage: "Demand Prep",
          text: "PL 03 - Demand Preparation",
          value: 1123
        },
        { stage: "Demand Prep", text: "PL 04 - Demand Review", value: 1124 },
        { stage: "Demand Prep", text: "PL 05 - Demand Sent", value: 1125 },
        { stage: "Negotiations", text: "PL 06 - Negotiations", value: 1126 },
        {
          stage: "Negotiations",
          text: "PL 07 - UIM Demand Preparation",
          value: 1127
        },
        { stage: "Negotiations", text: "PL 08 - UIM Demand Sent", value: 1128 },
        {
          stage: "Negotiations",
          text: "PL 09 - UIM Negotiations",
          value: 1129
        },
        { stage: "Litigation", text: "PL 10 - UIM Arbitration", value: 1165 },
        { stage: "Settled", text: "PL 11 - Incoming Fees", value: 1130 },
        {
          stage: "Settled",
          text: "PL 12 - Fees Disbursed or Lien and Balance Resolution",
          value: 1131
        },
        { stage: "Settled", text: "PL 13 - Long-term Trust", value: 1132 },
        { stage: "", text: "", value: "" }
      ],
      selectedCaseId: "",
      investigationDialog: false,
      mileageReimbursementDialog: false,
      medicalProviders: [],
      medicalProvidersLoading: false,
      medicalProvidersError: null,
      selectedAddresses: {},
      clientAddresses: [],
      clientAddressesLoading: false,
      clientAddressesError: null,
      selectedClientAddress: null,
      showClientAddressSelector: false,
      reimbursementResults: [],
      showReimbursementResults: false,
      copySuccess: false,
      mileageRate: 70,
      originalReimbursementResults: []
    };
  },
  components: {
    Investigation
  },
  created() {
    // Set up watcher for mileage reimbursement dialog
    this.watchDialogOpen();
  },
  mounted() {
    setTimeout(() => {
      if (!this.$refs.chart) return;
      this.$refs.chart.chart.reflow();
    }, 200);
  },
  computed: {
    ...mapGetters(["imitatedUser", "currentUser"]),
    filteredCases() {
      if (!this.user) return [];
      return filter(this.user.cases, c => {
        if (this.filterRefAndInactive) {
          return !["Referred Out Stage", "Inactive"].includes(c.stage);
        } else {
          return true;
        }
      });
    },
    isAdmin() {
      return hasRole(this.currentUser, "admin");
    },
    sortBy() {
      let saved = JSON.parse(localStorage.getItem("RyBase.inventorySortBy"));
      if (!saved) return ["dateOpened"];
      return saved;
    },
    sortDesc() {
      let saved = JSON.parse(localStorage.getItem("RyBase.inventorySortDesc"));
      if (!saved) return [true];
      return saved;
    },
    totalReimbursement() {
      if (
        !this.reimbursementResults ||
        this.reimbursementResults.length === 0
      ) {
        return 0;
      }

      return this.reimbursementResults.reduce((sum, result) => {
        return sum + parseFloat(result.reimbursement || 0);
      }, 0);
    },
    patientAddressFromResults() {
      if (
        !this.reimbursementResults ||
        this.reimbursementResults.length === 0
      ) {
        return "";
      }

      // All results have the same patient address, so we can use the first one
      return this.reimbursementResults[0].patient_address;
    },
    formattedReimbursementResultsText() {
      if (
        !this.reimbursementResults ||
        this.reimbursementResults.length === 0
      ) {
        return "";
      }

      let text = "Mileage Reimbursement Results\n\n";
      text += `Patient Address: ${this.patientAddressFromResults}\n\n`;
      text += "Provider\tVisits\tRound Trip Miles\tReimbursement\n";

      this.reimbursementResults.forEach(result => {
        const providerName = result.has_blank_address
          ? `${result.provider_name} (No Address)`
          : result.provider_name;

        text += `${providerName}\t${result.visits}\t${
          result.round_trip_miles
        }\t${this.formatCurrency(result.reimbursement)}\n`;
      });

      text += `\nTotal: ${this.formatCurrency(this.totalReimbursement)}`;

      return text;
    },
    formattedReimbursementResultsHTML() {
      if (
        !this.reimbursementResults ||
        this.reimbursementResults.length === 0
      ) {
        return "";
      }

      // Simplified HTML format that works better with Word
      let html = `
        <table border="1" cellspacing="0" cellpadding="5" style="border-collapse:collapse;">
          <tr style="background-color:#f2f2f2; font-weight:bold;">
            <th align="left">Provider</th>
            <th align="right">Visits</th>
            <th align="right">Round Trip Miles</th>
            <th align="right">Reimbursement</th>
          </tr>
      `;

      // Add table rows for each result
      this.reimbursementResults.forEach(result => {
        const providerName = result.has_blank_address
          ? `${result.provider_name} (No Address)`
          : result.provider_name;

        html += `
          <tr${
            result.has_blank_address ? ' style="background-color:#f9f9f9;"' : ""
          }>
            <td align="left">${providerName}</td>
            <td align="right">${result.visits}</td>
            <td align="right">${result.round_trip_miles}</td>
            <td align="right">${this.formatCurrency(result.reimbursement)}</td>
          </tr>
        `;
      });

      // Add total row
      html += `
          <tr style="font-weight:bold; background-color:#eeeeee;">
            <td colspan="3" align="right">Total:</td>
            <td align="right">${this.formatCurrency(
              this.totalReimbursement
            )}</td>
          </tr>
        </table>
        <p style="font-style:italic;">Patient Address: ${
          this.patientAddressFromResults
        }</p>
        <p style="font-style:italic;">Rate: ${this.mileageRate}¢ per mile</p>
      `;

      return html;
    },
    clientPrimaryAddress() {
      if (!this.clientAddresses || this.clientAddresses.length === 0)
        return null;

      // Find the primary address (all records have the same primary address)
      return this.clientAddresses[0];
    },
    clientAltAddresses() {
      if (!this.clientAddresses || this.clientAddresses.length === 0) return [];

      // Filter addresses that have alternate address data
      // Only include records where the alternate address is different from the primary
      return this.clientAddresses.filter(
        address =>
          address.alt_address &&
          address.alt_address.trim() !== "" &&
          address.alt_address !== address.p_address
      );
    },
    groupedProviders() {
      if (!this.medicalProviders || this.medicalProviders.length === 0)
        return [];

      // Group medical providers by name
      const providersMap = new Map();

      this.medicalProviders.forEach(provider => {
        const key = provider.provider_name;

        if (!providersMap.has(key)) {
          providersMap.set(key, {
            provider_name: provider.provider_name,
            provider_address: provider.provider_address,
            provider_city: provider.provider_city,
            provider_state: provider.provider_state,
            provider_zip: provider.provider_zip,
            visits: provider.visits,
            addresses: []
          });
        }

        // If this provider has an alternate address that's different from the primary, add it
        if (
          provider.alt_address &&
          provider.alt_address.trim() !== "" &&
          provider.alt_address !== provider.provider_address
        ) {
          providersMap.get(key).addresses.push({
            alt_address: provider.alt_address,
            alt_city: provider.alt_city,
            alt_state: provider.alt_state,
            alt_zip: provider.alt_zip,
            visits: provider.visits
          });
        }
      });

      // Convert map to array
      return Array.from(providersMap.values());
    },
    inventoryValue() {
      if (!this.user) return "";
      let settledStatuses = [
        "SET - Disbursed or Lien and Balance Resolution",
        "PL 11 = Fees Disbursed or Lien and Balance Resolution",
        "PL 12 = Long-term Trust"
      ];
      let cases = this.filteredCases;
      let nonSettled = filter(cases, c => {
        return settledStatuses.indexOf(c.status) === -1;
      });
      let casesGrouped = groupBy(nonSettled, "rank");
      delete casesGrouped["Unrated - Default"];
      delete casesGrouped["null"];
      let casesGroupedTotals = [];
      for (const [key, val] of Object.entries(casesGrouped)) {
        switch (key) {
          case "A++ (> $1M)":
            casesGroupedTotals.push({
              name: key,
              lowValue: val.length * 1000000,
              highValue: val.length * 1000000
            });
            break;
          case "A+ ($251k - $1M)":
            casesGroupedTotals.push({
              name: key,
              lowValue: val.length * 250000,
              highValue: val.length * 1000000
            });
            break;
          case "A ($101k - $250k)":
            casesGroupedTotals.push({
              name: key,
              lowValue: val.length * 101000,
              highValue: val.length * 250000
            });
            break;
          case "B ($51k - $100k)":
            casesGroupedTotals.push({
              name: key,
              lowValue: val.length * 51000,
              highValue: val.length * 100000
            });
            break;
          case "C ($15k - $50k)":
            casesGroupedTotals.push({
              name: key,
              lowValue: val.length * 15000,
              highValue: val.length * 50000
            });
            break;
          case "D (<$15k)":
            casesGroupedTotals.push({
              name: key,
              lowValue: val.length * 5000,
              highValue: val.length * 14000
            });
            break;
          default:
            console.log("Bad Value");
            console.log(key, val);
        }
      }
      let totalLowValue = casesGroupedTotals.reduce((prev, cur) => {
        return prev + cur.lowValue;
      }, 0);
      let totalHighValue = casesGroupedTotals.reduce((prev, cur) => {
        return prev + cur.highValue;
      }, 0);
      return {
        totalLowValue: accounting.formatMoney(totalLowValue),
        totalLowFees: accounting.formatMoney(totalLowValue * (1 / 3)),
        totalHighValue: accounting.formatMoney(totalHighValue),
        totalHighFees: accounting.formatMoney(totalHighValue * (1 / 3))
      };
    },
    rankChartOptions() {
      if (!this.user) return {};
      let cases = this.filteredCases;
      let casesGrouped = groupBy(cases, "rank");
      return {
        chart: {
          type: "bar",
          height: 100
        },
        title: {
          text: ""
        },
        credits: {
          enabled: false
        },
        xAxis: {
          visible: false,
          labels: {
            enabled: false
          }
        },
        yAxis: {
          min: 0,
          visible: false,
          title: {
            text: ""
          },
          labels: {
            enabled: false
          }
        },
        legend: {
          enabled: false
        },
        plotOptions: {
          series: {
            stacking: "normal"
          }
        },
        tooltip: {
          headerFormat: ""
        },
        series: [
          {
            name: "A++ (> $1M)",
            color: "#03A9F4",
            data: [
              casesGrouped["A++ (> $1M)"]
                ? casesGrouped["A++ (> $1M)"].length
                : 0
            ]
          },
          {
            name: "A+ ($251k - $1M)",
            color: "#2196F3",
            data: [
              casesGrouped["A+ ($251k - $1M)"]
                ? casesGrouped["A+ ($251k - $1M)"].length
                : 0
            ]
          },
          {
            name: "A ($101k - $250k)",
            color: "#3F51B5",
            data: [
              casesGrouped["A ($101k - $250k)"]
                ? casesGrouped["A ($101k - $250k)"].length
                : 0
            ]
          },
          {
            name: "B ($51k - $100k)",
            color: "#673AB7",
            data: [
              casesGrouped["B ($51k - $100k)"]
                ? casesGrouped["B ($51k - $100k)"].length
                : 0
            ]
          },
          {
            name: "C ($15k - $50k)",
            color: "#9C27B0",
            data: [
              casesGrouped["C ($15k - $50k)"]
                ? casesGrouped["C ($15k - $50k)"].length
                : 0
            ]
          },
          {
            name: "D (<$15k)",
            color: "#E91E63",
            data: [
              casesGrouped["D (<$15k)"] ? casesGrouped["D (<$15k)"].length : 0
            ]
          },
          {
            name: "Unrated - Default",
            color: "#F44336",
            data: [
              casesGrouped["Unrated - Default"]
                ? casesGrouped["Unrated - Default"].length
                : 0
            ]
          },
          {
            name: "No Rank",
            color: "#FF9800",
            data: [casesGrouped["null"] ? casesGrouped["null"].length : 0]
          }
        ]
      };
    }
  },
  methods: {
    formatValue,
    startWatching,
    stopWatching,
    isWatching,
    formatLastAccessDate(date) {
      if (!date) return "";
      let days = getDaysSinceToday(date);
      return `${date} (${days} days)`;
    },
    formatAddress(address, city, state, zip) {
      if (!address || address.trim() === "") return "No address";
      if (!city || city.trim() === "") return "No address";
      if (!state || state.trim() === "") return "No address";

      const zipPart = zip && zip.trim() !== "" ? ` ${zip}` : "";
      return `${address}, ${city}, ${state}${zipPart}`;
    },
    formatCurrency(value) {
      if (!value) return "$0.00";
      return accounting.formatMoney(parseFloat(value));
    },
    fetchMedicalProviders() {
      this.medicalProvidersLoading = true;
      this.medicalProvidersError = null;
      this.medicalProviders = [];

      this.$apollo
        .query({
          query: gql`
            query CaseMedicalProvidersAndAltAddresses($caseId: Int!) {
              case_medical_providers_and_alt_addresses(caseId: $caseId) {
                case_id
                provider_name
                provider_address
                provider_city
                provider_state
                provider_zip
                p_address
                p_city
                p_state
                p_zip
                visits
                alt_address
                alt_city
                alt_state
                alt_zip
              }
            }
          `,
          variables: {
            caseId: parseInt(this.selectedCaseId)
          }
        })
        .then(response => {
          this.medicalProviders =
            response.data.case_medical_providers_and_alt_addresses;
          this.medicalProvidersLoading = false;

          // Auto-select primary addresses for each provider
          this.autoSelectPrimaryAddresses();
        })
        .catch(error => {
          this.medicalProvidersError = error;
          this.medicalProvidersLoading = false;
          console.error("Error fetching medical providers:", error);
        });
    },
    fetchClientAddresses() {
      this.clientAddressesLoading = true;
      this.clientAddressesError = null;
      this.clientAddresses = [];
      this.selectedClientAddress = null;

      this.$apollo
        .query({
          query: gql`
            query ClientAddresses($caseId: Int!) {
              client_addresses(caseId: $caseId) {
                case_id
                client_name
                p_address
                p_city
                p_state
                p_zip
                alt_address
                alt_city
                alt_state
                alt_zip
              }
            }
          `,
          variables: {
            caseId: parseInt(this.selectedCaseId)
          }
        })
        .then(response => {
          this.clientAddresses = response.data.client_addresses;
          this.clientAddressesLoading = false;

          // Auto-select the primary client address
          this.autoSelectClientPrimaryAddress();
        })
        .catch(error => {
          this.clientAddressesError = error;
          this.clientAddressesLoading = false;
          console.error("Error fetching client addresses:", error);
        });
    },
    autoSelectClientPrimaryAddress() {
      if (this.clientPrimaryAddress) {
        this.selectedClientAddress = {
          client_name: this.clientPrimaryAddress.client_name,
          address: this.clientPrimaryAddress.p_address,
          city: this.clientPrimaryAddress.p_city,
          state: this.clientPrimaryAddress.p_state,
          zip: this.clientPrimaryAddress.p_zip,
          is_primary: true
        };
      }
    },
    calculateMileageReimbursement() {
      // Get the selected addresses for each provider
      const selectedProviderAddresses = Object.values(
        this.selectedAddresses
      ).filter(Boolean);

      if (
        selectedProviderAddresses.length === 0 ||
        !this.selectedClientAddress
      ) {
        // No addresses selected
        return;
      }

      // Format the client address
      const clientAddress = this.formatAddress(
        this.selectedClientAddress.address,
        this.selectedClientAddress.city,
        this.selectedClientAddress.state,
        this.selectedClientAddress.zip
      );

      // Separate providers with valid addresses from those with blank/missing addresses
      const providersWithValidAddresses = [];
      const providersWithBlankAddresses = [];

      selectedProviderAddresses.forEach(provider => {
        // Check if the provider has a valid address
        if (
          !provider.address ||
          provider.address.trim() === "" ||
          provider.address.trim() === "No address" ||
          !provider.city ||
          provider.city.trim() === "" ||
          !provider.state ||
          provider.state.trim() === ""
        ) {
          // Provider has blank/invalid address
          providersWithBlankAddresses.push(provider);
        } else {
          // Provider has valid address
          providersWithValidAddresses.push(provider);
        }
      });

      // Set loading state
      this.medicalProvidersLoading = true;

      // If we have providers with valid addresses, calculate their mileage
      const calculateValidAddresses = async () => {
        if (providersWithValidAddresses.length === 0) {
          return [];
        }

        // Update provider addresses to include the full formatted address
        // Only include fields that are expected by the GraphQL schema
        const providersWithFormattedAddresses = providersWithValidAddresses.map(
          provider => {
            return {
              provider_name: provider.provider_name,
              address: this.formatAddress(
                provider.address,
                provider.city,
                provider.state,
                provider.zip
              ),
              visits: provider.visits
            };
          }
        );

        try {
          // Send the mutation to calculate mileage reimbursement
          const response = await this.$apollo.mutate({
            mutation: gql`
              mutation CalculateMileageReimbursement(
                $input: mileage_reimbursement_input!
              ) {
                calculate_mileage_reimbursement(mileage_reimbursement: $input) {
                  provider_name
                  provider_address
                  patient_address
                  reimbursement
                  round_trip_miles
                  visits
                  error
                }
              }
            `,
            variables: {
              input: {
                client_address: clientAddress,
                provider_addresses: providersWithFormattedAddresses
              }
            }
          });

          return response.data.calculate_mileage_reimbursement;
        } catch (error) {
          console.error("Error calculating mileage reimbursement:", error);
          throw error;
        }
      };

      // Execute the calculation
      calculateValidAddresses()
        .then(results => {
          // Check for errors in the results from providers with valid addresses
          const errors = results.filter(result => result.error);

          // Create results for providers with blank addresses
          const blankAddressResults = providersWithBlankAddresses.map(
            provider => ({
              provider_name: provider.provider_name,
              provider_address: "No address available",
              patient_address: clientAddress,
              reimbursement: 0,
              round_trip_miles: 0,
              visits: provider.visits || 0,
              error: null,
              has_blank_address: true
            })
          );

          // Combine all results
          const allResults = [...results, ...blankAddressResults];

          // If there are errors from the API, show them but still continue with results
          if (errors.length > 0) {
            this.$store.dispatch("snackbar/showMessage", {
              message: `Warning: Some providers had calculation errors: ${errors[0].error}`,
              color: "warning"
            });
          }

          // Show warning if some providers had blank addresses
          if (providersWithBlankAddresses.length > 0) {
            this.$store.dispatch("snackbar/showMessage", {
              message: `${providersWithBlankAddresses.length} provider(s) had no address and were included with $0 reimbursement`,
              color: "info"
            });
          }

          // Format reimbursement values as currency for display
          const formattedResults = allResults.map(result => ({
            ...result,
            reimbursement: parseFloat(result.reimbursement || 0),
            // Store original values for recalculation
            original_round_trip_miles: parseFloat(result.round_trip_miles || 0),
            original_visits: parseInt(result.visits || 0)
          }));

          // Store the results for display
          this.reimbursementResults = formattedResults;
          // Store original results for recalculation
          this.originalReimbursementResults = JSON.parse(
            JSON.stringify(formattedResults)
          );

          // Clear loading state
          this.medicalProvidersLoading = false;

          // Close the input dialog
          this.mileageReimbursementDialog = false;

          // Show the results dialog
          this.showReimbursementResults = true;
        })
        .catch(error => {
          console.error("Error calculating mileage reimbursement:", error);

          // Show error message
          this.$store.dispatch("snackbar/showMessage", {
            message: `Error calculating reimbursement: ${error.message}`,
            color: "error"
          });

          // Clear loading state but keep dialog open so user can try again
          this.medicalProvidersLoading = false;

          // Don't close the dialog on error - let user retry or close manually
        });
    },
    updateSort(type, event) {
      if (type === "by") {
        localStorage.setItem("RyBase.inventorySortBy", JSON.stringify(event));
      } else if (type === "order") {
        localStorage.setItem("RyBase.inventorySortDesc", JSON.stringify(event));
      }
    },
    // Watch for dialog open to fetch medical providers
    watchDialogOpen() {
      this.$watch("mileageReimbursementDialog", newVal => {
        if (newVal && this.selectedCaseId) {
          // Reset selections when dialog opens
          this.selectedAddresses = {};
          this.showClientAddressSelector = false;

          // Fetch data
          this.fetchClientAddresses();
          this.fetchMedicalProviders();
        }
      });
    },
    saveRank(statusId) {
      if (!statusId) return false;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation InsertCaseRankStatus($status: CaseStatus!) {
              insertSaRankStatus(status: $status) {
                statusId
              }
            }
          `,
          variables: {
            status: {
              case_id: this.selectedCaseId,
              status_id: statusId
            }
          },
          update: (store, { data: { insertSaRankStatus } }) => {
            this.updateCache(store, insertSaRankStatus);
          }
        })
        .then(() => {})
        .catch(error => {
          console.error(error);
        });
      return false;
    },
    saveStatus(val, type, options, statusType) {
      if (!val) return false;
      this.$apollo
        .mutate({
          mutation: gql`
            mutation UpdateCaseStatus($status: CaseStatus!) {
              upsertSaStatus(status: $status) {
                statusId
              }
            }
          `,
          variables: {
            status: {
              case_id: this.selectedCaseId,
              status_id: val,
              type: statusType
            }
          },
          update: (store, { data: { upsertSaStatus } }) => {
            this.updateStatusCache(store, upsertSaStatus, "status");
          }
        })
        .then(() => {
          this.$refs["inventory-table"].$el.click();
        })
        .catch(error => {
          console.error(error);
        });
      return false;
    },
    updateCache(store, insertData) {
      let id = this.imitatedUser
        ? this.imitatedUser.toString()
        : this.$route.params.id;
      let caseId = this.selectedCaseId;
      const data = store.readQuery({
        query: DASH_INVENTORY_LIST,
        variables: {
          id
        }
      });
      let index = findIndex(data.user.cases, c => {
        return c.caseId === caseId;
      });
      let newRank = this.rankText(insertData.statusId);
      data.user.cases[index].rank = newRank;
      store.writeQuery({
        query: DASH_INVENTORY_LIST,
        variables: {
          id
        },
        data
      });
    },
    updateStatusCache(store, insertData, field) {
      let id = this.imitatedUser
        ? this.imitatedUser.toString()
        : this.$route.params.id;
      let caseId = this.selectedCaseId;
      const data = store.readQuery({
        query: DASH_INVENTORY_LIST,
        variables: {
          id
        }
      });
      let index = findIndex(data.user.cases, c => {
        return c.caseId === caseId;
      });
      data.user.cases[index][field] = this.getText(
        insertData.statusId,
        this.statusOptions
      );
      data.user.cases[index].stage = this.getStage(
        insertData.statusId,
        this.statusOptions
      );
      store.writeQuery({
        query: DASH_INVENTORY_LIST,
        variables: {
          id
        },
        data
      });
    },
    rankValue(rank) {
      let ranks = groupBy(this.rankOptions, "text");
      if (!ranks[rank]) return null;
      return ranks[rank][0].value;
    },
    rankText(rank) {
      let ranks = groupBy(this.rankOptions, "value");
      return ranks[rank][0].text;
    },
    openRank(caseId) {
      this.selectedCaseId = caseId;
      return false;
    },
    closeRank() {
      this.selectedCaseId = "";
      return false;
    },
    getValue(val, options) {
      let values = groupBy(options, "text");
      if (!values[val]) return null;
      return values[val][0].value;
    },
    getText(val, options) {
      let values = groupBy(options, "value");
      return values[val][0].text;
    },
    getStage(val, options) {
      let values = groupBy(options, "value");
      return values[val][0].stage;
    },
    openEditDialog(caseId) {
      this.selectedCaseId = caseId;
      return false;
    },
    closeEditDialog() {
      this.selectedCaseId = "";
      return false;
    },
    closeInvestigationDialog() {
      this.selectedCaseId = "";
      this.investigationDialog = false;
    },
    getSelectedAddressDisplay(provider) {
      const selectedAddress = this.selectedAddresses[provider.provider_name];

      if (selectedAddress) {
        return this.formatAddress(
          selectedAddress.address,
          selectedAddress.city,
          selectedAddress.state,
          selectedAddress.zip
        );
      }

      return "No address selected";
    },
    hasValidAddress(provider) {
      // Check if the provider has a valid primary address
      const hasValidPrimary =
        provider.provider_address &&
        provider.provider_address.trim() !== "" &&
        provider.provider_city &&
        provider.provider_city.trim() !== "" &&
        provider.provider_state &&
        provider.provider_state.trim() !== "";

      // Check if the provider has any valid alternate addresses
      const hasValidAlternate =
        provider.addresses &&
        provider.addresses.some(
          address =>
            address.alt_address &&
            address.alt_address.trim() !== "" &&
            address.alt_city &&
            address.alt_city.trim() !== "" &&
            address.alt_state &&
            address.alt_state.trim() !== ""
        );

      return hasValidPrimary || hasValidAlternate;
    },
    autoSelectPrimaryAddresses() {
      // Create a temporary object to store provider selections
      const selections = {};

      // Group providers by name to get unique providers
      this.groupedProviders.forEach(provider => {
        // Check if the provider has a valid primary address
        const hasValidPrimaryAddress =
          provider.provider_address &&
          provider.provider_address.trim() !== "" &&
          provider.provider_city &&
          provider.provider_city.trim() !== "" &&
          provider.provider_state &&
          provider.provider_state.trim() !== "";

        // Auto-select the primary address for each provider (even if blank, so user can see it)
        selections[provider.provider_name] = {
          provider_name: provider.provider_name,
          address: provider.provider_address || "",
          city: provider.provider_city || "",
          state: provider.provider_state || "",
          zip: provider.provider_zip || "",
          visits: provider.visits,
          is_primary: true,
          has_valid_address: hasValidPrimaryAddress
        };
      });

      // Set the selections
      this.selectedAddresses = selections;
    },
    copyReimbursementResultsToClipboard() {
      try {
        // Create an HTML blob for the clipboard
        const htmlBlob = new Blob([this.formattedReimbursementResultsHTML], {
          type: "text/html"
        });

        // Create a plain text version for fallback
        const textBlob = new Blob([this.formattedReimbursementResultsText], {
          type: "text/plain"
        });

        // Try using modern clipboard API with HTML format
        if (
          navigator.clipboard &&
          navigator.clipboard.write &&
          typeof ClipboardItem !== "undefined"
        ) {
          navigator.clipboard
            .write([
              // eslint-disable-next-line no-undef
              new ClipboardItem({
                "text/html": htmlBlob,
                "text/plain": textBlob
              })
            ])
            .then(() => {
              this.copySuccess = true;
              setTimeout(() => {
                this.copySuccess = false;
              }, 3000);
            })
            .catch(err => {
              console.error("Failed to copy using Clipboard API: ", err);
              this.fallbackCopyToClipboard();
            });
        } else {
          this.fallbackCopyToClipboard();
        }
      } catch (err) {
        console.error("Failed to copy text: ", err);
        this.fallbackCopyToClipboard();
      }
    },
    fallbackCopyToClipboard() {
      try {
        // Create a temporary container
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = this.formattedReimbursementResultsHTML;
        tempDiv.style.position = "fixed";
        tempDiv.style.left = "-9999px";
        document.body.appendChild(tempDiv);

        // Create a range and select the content
        const range = document.createRange();
        range.selectNode(tempDiv);

        // Clear current selection and add our range
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        // Execute copy command
        const successful = document.execCommand("copy");

        // Clean up
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);

        if (successful) {
          this.copySuccess = true;
          setTimeout(() => {
            this.copySuccess = false;
          }, 3000);
        } else {
          throw new Error("Copy command was unsuccessful");
        }
      } catch (err) {
        console.error("Fallback: Failed to copy", err);

        // Final fallback: try copying just the text
        try {
          const textarea = document.createElement("textarea");
          textarea.value = this.formattedReimbursementResultsText;
          textarea.style.position = "fixed";
          textarea.style.left = "-9999px";
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand("copy");
          document.body.removeChild(textarea);

          this.$store.dispatch("snackbar/showMessage", {
            message:
              "Copied as plain text. HTML formatting not supported in your browser.",
            color: "warning"
          });
        } catch (finalErr) {
          this.$store.dispatch("snackbar/showMessage", {
            message:
              "Could not copy to clipboard. Please try selecting the text manually.",
            color: "error"
          });
        }
      }
    },
    recalculateReimbursement() {
      // Ensure mileage rate is a valid number
      const rate = parseFloat(this.mileageRate);
      if (isNaN(rate) || rate <= 0) {
        this.mileageRate = 70;
        return;
      }

      // Recalculate the reimbursement based on the new rate
      if (
        this.originalReimbursementResults &&
        this.originalReimbursementResults.length > 0
      ) {
        this.reimbursementResults = this.originalReimbursementResults.map(
          result => {
            const miles = result.original_round_trip_miles;
            const visits = result.original_visits;

            // Calculate new reimbursement amount based on rate (converting from cents to dollars)
            const newReimbursement = miles * visits * (rate / 100);

            return {
              ...result,
              reimbursement: newReimbursement
            };
          }
        );
      }
    }
  },
  apollo: {
    user: {
      query: DASH_INVENTORY_LIST,
      variables() {
        let id = this.imitatedUser
          ? this.imitatedUser.toString()
          : this.$route.params.id;
        return {
          id
        };
      }
    }
  }
};
</script>

<style scoped>
.total-settlement-value {
  width: 90%;
  text-align: center;
}
.total-fee-value {
  width: 90%;
  text-align: center;
}
</style>
